import { useQuery } from "@tanstack/react-query"
import { api, unwrap } from "@/shared/api/client"
import { listDepositDrafts, listDepositsModeration, listEventsModeration } from "@/shared/api/rgb"
import { showOrganizer, type CabinetAccess, type CabinetId, type RoleCabinetId } from "./cabinetNav"

export type CabinetCounters = Partial<Record<CabinetId, number>>

const COUNTER_STALE_MS = 60_000

const useCount = (key: string, enabled: boolean, load: () => Promise<number>) =>
  useQuery({
    queryKey: ["cabinet", "counters", key],
    queryFn: load,
    enabled,
    staleTime: COUNTER_STALE_MS,
    retry: false,
  }).data

/** Сколько дел ждёт в каждом кабинете; считается из существующих списков API. */
export const useCabinetCounters = (access: CabinetAccess): CabinetCounters => {
  const has = (id: RoleCabinetId) => access.available.includes(id)
  const depositHost: RoleCabinetId | null = has("union") ? "union" : has("catalog") ? "catalog" : null

  const drafts = useCount("writer-drafts", has("writer"), async () => (await listDepositDrafts()).items.length)
  const applications = useCount(
    "union-applications",
    has("union"),
    async () => (await unwrap<{ items: unknown[] }>(api.GET("/union/applications"))).items.length,
  )
  const errorReports = useCount(
    "catalog-errors",
    has("catalog"),
    async () =>
      (
        await unwrap<{ total: number }>(
          api.GET("/admin/error-reports", { params: { query: { status: "new", page: 1, page_size: 1 } } }),
        )
      ).total,
  )
  const deposits = useCount(
    "deposit-moderation",
    Boolean(depositHost) && access.canModerateDeposits,
    async () => (await listDepositsModeration({ page: 1, page_size: 1 })).total,
  )
  const events = useCount(
    "event-moderation",
    showOrganizer(access) && access.canModerateEvents,
    async () => (await listEventsModeration({ page: 1, page_size: 1 })).total,
  )

  const counters: CabinetCounters = {}
  const add = (id: CabinetId | null, value: number | undefined) => {
    if (!id || value === undefined) return
    counters[id] = (counters[id] ?? 0) + value
  }
  if (has("writer")) add("writer", drafts)
  if (has("union")) add("union", applications)
  if (has("catalog")) add("catalog", errorReports)
  add(depositHost, deposits)
  if (access.canModerateEvents) add("organizer", events)
  return counters
}
